import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pagination } from "../../../components/pagination";
import { TemplateActionTypes } from "../../../types/global";
import { QueryBanner } from "../../../components/queryBanner/queryBanner";
import { userManagementForm } from "../../../formConfig/queryBanner/portalUsers";
import { Props } from "./interface";
import { AppContext, AppContextType } from "../../../constants/context";
import { usePageDataQuery } from "../../../hooks/usePageDataQuery";
import { tableHeaders } from "../../../constants/tableHeaders";
import { AdvancedTable } from "../../../components/advancedTable";
import { Portal } from "../../../constants/user";
import { portalUserStatuses } from "../../../constants";
import { routePaths, Routes } from "../../../constants/routes";

export const UserManagementPageTemplate = ({
  pageTitleText,
  noResultsText,
  queryBannerText = "Search and filter portal users:",
  queryBannerFilterConfig,
  tableCaptionText,
  tableId = "UserAdminAppTable",
  tableData: tableDataProps,
  sortableColumns: sortableColumnsProps = [],
  hiddenColumns: hiddenColumnsProps = [],
  pagination: paginationProps,
  apis,
  columnOrder,
  error: errorProp,
  viewText,
  dataItemKey,
  columnHeaderTextMap = tableHeaders,
}: Props): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const appContext = useContext<AppContextType>(AppContext);
  const { userRoles } = appContext;
  const navigate = useNavigate();

  // Override pagination with URL query parameter if present
  const urlPageNumber = searchParams.get("pageNumber");
  const pageNumberFromUrl = urlPageNumber ? parseInt(urlPageNumber, 10) : null;
  const initialPagination =
    pageNumberFromUrl && pageNumberFromUrl > 0
      ? { ...paginationProps, pageNumber: pageNumberFromUrl }
      : paginationProps;

  const {
    pageDataRequestError,
    activeSort,
    paginationState,
    tableDataState,
    onFormSubmit,
    onPageChange,
    onSort,
  } = usePageDataQuery(
    {
      formValues,
      tableDataProps,
      paginationProps: initialPagination,
      initialError: errorProp,
      apis,
      columnOrder,
    },
    { setFormValues }
  );

  // Custom page change handler that updates URL
  const handlePageChange = (pageNumber: number) => {
    setSearchParams({ pageNumber: pageNumber.toString() });
    return onPageChange(pageNumber);
  };

  const { totalCount, totalPages, pageNumber, pageSize } =
    paginationState ?? {};
  const hasTableData = tableDataState?.length > 0;

  const actions: TemplateActionTypes = {
    table: [
      {
        text: (data) => {
          const { portal, status } = data;

          if (
            portal === Portal.ClientPortal &&
            status !== portalUserStatuses.Active &&
            status !== portalUserStatuses.ResubmittedActive
          ) {
            return "View";
          }

          return viewText;
        },
        srText: (data) => data[dataItemKey] ?? "Item",
        callback: async (data: any) => {
          const { azureUserId } = data ?? {};
          if (azureUserId) {
            navigate(`${routePaths[Routes.PORTAL_USERS]}/${azureUserId}`);
          }
        },
      },
    ],
    modal: [],
  };

  return (
    <>
      <div className="u-bg-burnham-green u-text-center u-text-white u-m-0 u-p-2 u-to-viewport-edge-x">
        <h2>{pageTitleText}</h2>
      </div>
      <QueryBanner
        title={queryBannerText}
        formConfig={userManagementForm(userRoles, queryBannerFilterConfig)}
        onFormSubmit={onFormSubmit}
      />
      <div className="u-flex u-flex-col u-justify-center u-my-5 u-w-full u-overflow-x-auto">
        {pageDataRequestError ? (
          <>{pageDataRequestError}</>
        ) : !hasTableData ? (
          <>{noResultsText}</>
        ) : (
          <AdvancedTable
            data={tableDataState}
            caption={tableCaptionText}
            id={tableId}
            sortableColumns={sortableColumnsProps}
            hiddenColumns={hiddenColumnsProps}
            sortFunction={onSort}
            activeSort={activeSort}
            columnHeaderTextMap={columnHeaderTextMap}
            actions={actions.table}
          />
        )}
      </div>
      {hasTableData && (
        <Pagination
          pageNumber={pageNumber}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          numberOfResults={tableDataState.length}
          setPageNumberAction={handlePageChange}
          ariaProps={{
            controls: tableId,
          }}
        />
      )}
    </>
  );
};
